import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  GlobalStyles,
  IconButton,
  List,
  Paper,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { PartyPopper } from "lucide-react";
import { motion } from "framer-motion";
import sh from "bundles/common/utils/sh";
import DashboardMetricCard from "./dashboard/DashboardMetricCard";
import TopPerformersCard from "./dashboard/TopPerformersCard";
import QueueItem from "./dashboard/QueueItem";
import PipelinesCard from "./dashboard/PipelinesCard";
import LogCallCard from "./dashboard/LogCallCard";
import PersonalNotesCard from "./dashboard/PersonalNotesCard";
import DailySummaryCard from "./dashboard/CallSummaryCard";
import CreatePipelineDrawer from "./dashboard/CreatePipelineDrawer";
import ActivityFeedCard from "./dashboard/ActivityFeedCard";
import FollowedActivityCard from "./dashboard/FollowedActivityCard";
import ProfileDetailPanel from "./common/ProfileDetailPanel";
import ConfirmDialog from "./common/ConfirmDialog";
import { useCandidateModal } from "./common/CandidateModalProvider";
import DateRangeSelect from "./common/DateRangeSelect";
import { appendFormValue } from "bundles/Internal/utils/candidateFormData";

const daySubtitles = [
  "Sunday is just a gentle reminder that Monday exists",
  "Ah yes, Monday. The sequel nobody asked for.",
  "Survived Monday. Now what?",
  "We're halfway through... whatever this is",
  "Thursday: running on 12% battery and bad decisions.",
  "It's Friday, then Saturday, Sunday (what?)",
  "Seriously? Go take some air. The profiles will still be here Monday.",
];

// Logic to prioritize the call queue based on follow-up times and outcomes. Profiles with overdue follow-ups are ranked highest,
// followed by those with follow-ups today, then tomorrow, then those without follow-ups. Within each category,
// profiles are sorted by follow-up time (earliest first) and then by ID (newest first).
const FOLLOW_UP_EXCLUDED_OUTCOMES = ["not_interested", "applied", "voicemail", "to_be_checked", "signed_up", "email_sent"];

function prioritizeQueue(queue = []) {
  const now = new Date();
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);

  const tomorrowEnd = new Date(todayEnd);
  tomorrowEnd.setDate(todayEnd.getDate() + 1);

  const filtered = queue.filter((profile) => !FOLLOW_UP_EXCLUDED_OUTCOMES.includes(profile?.last_action));

  return [...filtered].sort((a, b) => {
    const followA = a?.follow_up_at ? new Date(a.follow_up_at) : null;
    const followB = b?.follow_up_at ? new Date(b.follow_up_at) : null;
    const hasFollowA = followA instanceof Date && !Number.isNaN(followA.getTime());
    const hasFollowB = followB instanceof Date && !Number.isNaN(followB.getTime());

    const rankFor = (hasFollow, follow) => {
      if (!hasFollow) return 4;
      if (follow < now) return 0;
      if (follow <= todayEnd) return 1;
      if (follow <= tomorrowEnd) return 2;
      return 3;
    };

    const rankA = rankFor(hasFollowA, followA);
    const rankB = rankFor(hasFollowB, followB);
    if (rankA !== rankB) return rankA - rankB;

    const timeA = hasFollowA ? followA.getTime() : Number.MAX_SAFE_INTEGER;
    const timeB = hasFollowB ? followB.getTime() : Number.MAX_SAFE_INTEGER;
    if (timeA !== timeB) return timeA - timeB;

    return Number(b?.id || 0) - Number(a?.id || 0);
  });
}


const InternalDashboardPage = () => {
  const navigate = useNavigate();

  const authenticity_token = useSelector((state) => state.authenticity_token);
  const currentUser = useSelector((state) => state.current_internal_user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [, setLoggingCall] = useState(false);
  const [, setSavingProfileNotes] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [showCreatePipelineDrawer, setShowCreatePipelineDrawer] = useState(false);
  const [isArchivingPipeline, setIsArchivingPipeline] = useState(false);
  const [archiveConfirmId, setArchiveConfirmId] = useState(null);
  const { openCreateCandidate, updateCandidateModal } = useCandidateModal();
  const [period, setPeriod] = useState("daily");
  const [customRange, setCustomRange] = useState({ startDate: null, endDate: null });
  const [periodStats, setPeriodStats] = useState(null);
  const [periodComparison, setPeriodComparison] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);

  const PERIODS = ["daily", "weekly", "monthly", "custom"];
  const COMPARISON_LABELS = { daily: "vs yesterday", weekly: "vs last week", monthly: "vs last month", custom: "vs previous range" };

  const fetchStats = useCallback(() => {
    const params = { period };
    if (period === "custom" && customRange.startDate && customRange.endDate) {
      params.start_date = customRange.startDate;
      params.end_date = customRange.endDate;
    }
    sh.get("/internal/dashboard/stats", { params })
      .then((res) => {
        setPeriodStats(res.data?.stats || null);
        setPeriodComparison(res.data?.comparison || null);
      })
      .catch(() => {});
  }, [period, customRange]);

  const fetchLeaderboard = useCallback(() => {
    const params = { period };
    if (period === "custom" && customRange.startDate && customRange.endDate) {
      params.start_date = customRange.startDate;
      params.end_date = customRange.endDate;
    }
    sh.get("/internal/dashboard/leaderboard", { params })
      .then((res) => {
        setLeaderboard(Array.isArray(res.data?.leaderboard) ? res.data.leaderboard : []);
      })
      .catch(() => {});
  }, [period, customRange]);

  useEffect(() => {
    if (period === "custom" && (!customRange.startDate || !customRange.endDate)) return;
    fetchStats();
    fetchLeaderboard();
  }, [fetchStats, fetchLeaderboard, period, customRange]);

  const fetchDashboard = useCallback((withLoader = true) => {
    if (withLoader) setLoading(true);
    setError("");

    sh.get("/internal/dashboard")
      .then((res) => {
        setDashboard(res.data || {});
      })
      .catch(() => {
        setError("Failed to load dashboard data.");
      })
      .finally(() => {
        if (withLoader) setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const queue = useMemo(() => prioritizeQueue(dashboard?.profile_queue || []), [dashboard?.profile_queue]);
  const visibleQueue = showAll ? queue : queue.slice(0, 3);
  const pipelines = dashboard?.profile_pipelines || [];
  const personalNotes = dashboard?.personal_notes || [];
  const outcomeSummary = dashboard?.outcome_summary || {};
  const canArchiveSharedPipelines = currentUser?.role === "admin" || currentUser?.role === "coordinator";

  const openCreatePipelineDrawer = useCallback(() => {
    setShowCreatePipelineDrawer(true);
  }, []);

  const selectProfile = useCallback((profile) => {
    if (!profile?.id) return;

    setSelectedProfile((prev) => ({ ...prev, ...profile }));

    sh.get(`/internal/external_profiles/${profile.id}`)
      .then((res) => {
        setSelectedProfile((prev) => ({ ...prev, ...(res.data || {}) }));
      })
      .catch(() => {
        setSelectedProfile((prev) => ({ ...prev, ...profile }));
      });
  }, []);

  const onLogCall = useCallback((outcome, time, notes, followUpAt) => {
    if (!selectedProfile?.id || !outcome) return;
    setLoggingCall(true);

    const callLogPayload = { outcome };

    if (followUpAt) {
      callLogPayload.follow_up_at = followUpAt;
    } else if (time && outcome === "follow_up_today") {
      const [h, m] = time.split(":");
      const date = new Date();
      date.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
      callLogPayload.follow_up_at = date.toISOString();
    }

    if (notes) callLogPayload.notes = notes;

    sh.post(`/internal/external_profiles/${selectedProfile.id}/call_logs`, {
      authenticity_token,
      call_log: callLogPayload,
    })
      .then((res) => {
        const updatedProfile = res?.data?.profile;
        if (updatedProfile) {
          setSelectedProfile(updatedProfile);
          setDashboard((prev) => {
            if (!prev) return prev;

            const queueItems = Array.isArray(prev.profile_queue) ? prev.profile_queue : [];
            const exists = queueItems.some((item) => String(item.id) === String(updatedProfile.id));
            const nextQueue = exists
              ? queueItems.map((item) => (String(item.id) === String(updatedProfile.id) ? { ...item, ...updatedProfile } : item))
              : [updatedProfile, ...queueItems];

            return {
              ...prev,
              profile_queue: nextQueue,
            };
          });
        }
        fetchDashboard(false);
      })
      .catch(() => {
        // UI-only error handling for now.
      })
      .finally(() => setLoggingCall(false));
  }, [authenticity_token, fetchDashboard, selectedProfile]);

  const onQuickLogCall = useCallback((profileId, outcome, time, notes, followUpAt) => {
    if (!profileId || !outcome) return;

    const callLogPayload = { outcome };

    if (followUpAt) {
      callLogPayload.follow_up_at = followUpAt;
    } else if (outcome === "follow_up_today") {
      const date = new Date();
      if (time) {
        const [h, m] = time.split(":");
        date.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
      } else {
        date.setHours(23, 59, 0, 0);
      }
      callLogPayload.follow_up_at = date.toISOString();
    }

    if (notes) callLogPayload.notes = notes;

    sh.post(`/internal/external_profiles/${profileId}/call_logs`, {
      authenticity_token,
      call_log: callLogPayload,
    })
      .then((res) => {
        const updatedProfile = res?.data?.profile;
        if (updatedProfile) {
          if (selectedProfile && String(selectedProfile.id) === String(profileId)) {
            setSelectedProfile(updatedProfile);
          }
          setDashboard((prev) => {
            if (!prev) return prev;
            const queueItems = Array.isArray(prev.profile_queue) ? prev.profile_queue : [];
            const exists = queueItems.some((item) => String(item.id) === String(updatedProfile.id));
            const nextQueue = exists
              ? queueItems.map((item) => (String(item.id) === String(updatedProfile.id) ? { ...item, ...updatedProfile } : item))
              : [updatedProfile, ...queueItems];
            return { ...prev, profile_queue: nextQueue };
          });
        }
        fetchDashboard(false);
      })
      .catch(() => {});
  }, [authenticity_token, fetchDashboard, selectedProfile]);

  const addPersonalNote = useCallback((text) => {
    if (!text) return;

    sh.post("/internal/personal_notes", {
      authenticity_token,
      internal_personal_note: { content: text },
    })
      .then(() => {
        fetchDashboard(false);
      })
      .catch(() => {});
  }, [authenticity_token, fetchDashboard]);

  const removePersonalNote = useCallback((id) => {
    sh.delete(`/internal/personal_notes/${id}`, {
      data: { authenticity_token },
    }).then(() => fetchDashboard(false));
  }, [authenticity_token, fetchDashboard]);

  const archivePipeline = useCallback((pipelineId) => {
    setArchiveConfirmId(pipelineId);
  }, []);

  const sortPinnedFirst = useCallback((list) =>
    [...list].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return new Date(b.created_at) - new Date(a.created_at);
    }),
  []);

  const togglePinPipeline = useCallback((pipeline) => {
    const wasPinned = pipeline.pinned;
    const url = `/internal/profile_pipelines/${pipeline.id}/toggle_pin`;

    setDashboard((prev) => {
      if (!prev) return prev;
      const update = (list) => sortPinnedFirst(list.map((p) => (p.id === pipeline.id ? { ...p, pinned: !wasPinned } : p)));
      return {
        ...prev,
        profile_pipelines: update(prev.profile_pipelines || []),
      };
    });

    sh.post(url, { authenticity_token, pinned: !wasPinned })
      .catch(() => {
        setDashboard((prev) => {
          if (!prev) return prev;
          const revert = (list) => sortPinnedFirst(list.map((p) => (p.id === pipeline.id ? { ...p, pinned: wasPinned } : p)));
          return {
            ...prev,
            profile_pipelines: revert(prev.profile_pipelines || []),
          };
        });
      });
  }, [authenticity_token, sortPinnedFirst]);

  const confirmArchivePipeline = useCallback(() => {
    if (!archiveConfirmId || isArchivingPipeline) return;

    setIsArchivingPipeline(true);
    setArchiveConfirmId(null);

    const removeFromList = (list) => list.filter((p) => p.id !== archiveConfirmId);
    setDashboard((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        profile_pipelines: removeFromList(prev.profile_pipelines || []),
      };
    });

    sh.delete(`/internal/profile_pipelines/${archiveConfirmId}`, {
      data: { authenticity_token },
    })
      .then(() => {
        fetchDashboard(false);
      })
      .catch(() => {
        setError("Failed to archive pipeline.");
        fetchDashboard(false);
      })
      .finally(() => {
        setIsArchivingPipeline(false);
      });
  }, [authenticity_token, fetchDashboard, isArchivingPipeline, archiveConfirmId]);

  const handleCreateCandidate = useCallback((values, { reset, resumeFile, autoEmail, close } = {}) => {
    setError(null);

    const appendValue = appendFormValue;

    const externalProfileId = selectedProfile?.id;

    const applyProfileLink = (candidate) => {
      if (!externalProfileId || !candidate?.id) return;
      setSelectedProfile((prev) =>
        prev && String(prev.id) === String(externalProfileId)
          ? { ...prev, candidate_id: candidate.id }
          : prev
      );
      setDashboard((prev) => {
        if (!prev) return prev;
        const queueItems = Array.isArray(prev.profile_queue) ? prev.profile_queue : [];
        return {
          ...prev,
          profile_queue: queueItems.map((item) =>
            String(item.id) === String(externalProfileId) ? { ...item, candidate_id: candidate.id } : item
          ),
        };
      });
    };

    const doPost = (payload) =>
      sh.post("/internal/candidates", payload)
        .then((res) => {
          const candidate = res.data;
          applyProfileLink(candidate);
          return candidate;
        })
        .then(() => {
          reset?.();
          close?.();
        })
        .catch((err) => {
          const messages = err?.response?.data?.errors;
          if (err?.response?.status === 422 && Array.isArray(messages) && messages.length > 0) {
            setError(messages.join(", "));
          } else {
            setError("Failed to create candidate.");
          }
        });

    if (!resumeFile) {
      return doPost({
        candidate: values,
        authenticity_token,
        auto_email: autoEmail,
        ...(externalProfileId ? { external_profile_id: externalProfileId } : {}),
      });
    }

    const formData = new FormData();
    formData.append("authenticity_token", authenticity_token);
    Object.entries(values || {}).forEach(([k, v]) => appendValue(formData, `candidate[${k}]`, v));
    formData.append("candidate[resume]", resumeFile);
    if (autoEmail && typeof autoEmail === "object") {
      Object.entries(autoEmail).forEach(([k, v]) => appendValue(formData, `auto_email[${k}]`, v));
    }
    if (externalProfileId) {
      formData.append("external_profile_id", String(externalProfileId));
    }
    return doPost(formData);
  }, [authenticity_token, selectedProfile]);

  const handleSignUpToCandidate = useCallback((profile) => {
    openCreateCandidate({
      onSubmit: handleCreateCandidate,
      defaultValues: {
        first_name: profile.first_name || "",
        last_name: profile.last_name || "",
        email: profile.email || "",
        phone: profile.phone || "",
        region: profile.region || "",
        desired_job_title: profile.desired_title || "",
      },
    });

    if (profile.cv_url) {
      fetch(profile.cv_url)
        .then((res) => res.blob())
        .then((blob) => {
          const name = profile.cv_url.split("/").pop()?.split("?")[0] || "cv.pdf";
          updateCandidateModal({ defaultResumeFile: new File([blob], name, { type: blob.type }) });
        });
    }
  }, [openCreateCandidate, updateCandidateModal, handleCreateCandidate]);

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <>
      <GlobalStyles
        styles={{
          "@keyframes tick-bounce": {
            "0%": { transform: "scale(1)" },
            "30%": { transform: "scale(1.15)" },
            "60%": { transform: "scale(0.95)" },
            "100%": { transform: "scale(1)" },
          },
        }}
      />

      <Box sx={{ minHeight: "100vh", width: "100%", backgroundColor: "hsl(140 10% 95%)" }}>
        <Box sx={{ px: { xs: 2, md: 3 }, py: 4, maxWidth: 1280, mx: "auto", width: "100%" }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>
          )}

          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0 }}
            sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
          >
            <Box>
              <Typography sx={{ fontSize: 24, fontWeight: 700, letterSpacing: -0.2, color: "hsl(150 30% 12%)", lineHeight: 1.2, fontFamily: "Segoe UI Bold, sans-serif, system-ui" }}>
                Good morning, {currentUser?.first_name || "you"}
              </Typography>
              <Typography sx={{ fontSize: 14, color: "hsl(150 10% 46%)", mt: 0.5, fontFamily: "Segoe UI regular, sans-serif, system-ui" }}>
                {daySubtitles[new Date().getDay()]}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {period === "custom" && (
                <DateRangeSelect
                  startDate={customRange.startDate}
                  endDate={customRange.endDate}
                  onChange={(range) => setCustomRange(range)}
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  backgroundColor: "hsl(140 6% 90%)",
                  borderRadius: "8px",
                  p: "2px",
                  border: "1px solid hsl(140 8% 88%)",
                }}
              >
                {PERIODS.map((p) => (
                  <Box
                    key={p}
                    component="button"
                    onClick={() => setPeriod(p)}
                    sx={{
                      all: "unset",
                      px: "16px",
                      py: "6px",
                      fontSize: 12,
                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      borderRadius: "6px",
                      cursor: "pointer",
                      transition: "all 150ms ease",
                      color: period === p ? "hsl(150 30% 12%)" : "hsl(150 10% 46%)",
                      backgroundColor: period === p ? "#ffffff" : "transparent",
                      boxShadow: period === p ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                      "&:hover": { color: "hsl(150 30% 12%)" },
                    }}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut", delay: 0.08 }}
            sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 2 }}
          >
            <DashboardMetricCard title="Total calls" value={periodStats?.total_calls ?? 0} delta={periodComparison?.total_calls ?? 0} comparisonLabel={COMPARISON_LABELS[period]} barColor="hsl(142 71% 45%)" />
            <DashboardMetricCard title="Connect rate" value={periodStats?.connect_rate ?? 0} suffix="%" delta={periodComparison?.connect_rate ?? 0} comparisonLabel={COMPARISON_LABELS[period]} barColor="hsl(38 92% 50%)" />
            <DashboardMetricCard title="Follow-ups set" value={periodStats?.follow_ups_set ?? 0} delta={periodComparison?.follow_ups_set ?? 0} comparisonLabel={COMPARISON_LABELS[period]} barColor="hsl(217 91% 55%)" />
            <DashboardMetricCard title="Sign rate" value={periodStats?.sign_rate ?? 0} suffix="%" delta={periodComparison?.sign_rate ?? 0} comparisonLabel={COMPARISON_LABELS[period]} barColor="hsl(0 72% 51%)" />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(5, minmax(0, 1fr))" }, gap: 4, mt: 4 }}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut", delay: 0.16 }}
              sx={{ gridColumn: { xs: "span 1", lg: "span 3" } }}
            >
              <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>Call queue</Typography>
                <Chip
                  label={`${queue.length} calls`}
                  sx={{
                    height: 20,
                    borderRadius: "calc(-2px + 0.75rem)",
                    backgroundColor: "hsl(217 91% 95%)",
                    color: "hsl(217 70% 30%)",
                    fontSize: 12,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                  }}
                />
              </Box>

              {queue.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: "0.75rem",
                    border: "1px solid hsl(140 8% 88%)",
                    p: 4,
                    textAlign: "center",
                    backgroundColor: "#ffffff",
                  }}
                  >
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
                    <PartyPopper size={32} color="hsl(150 38% 17%)" style={{ animation: "tick-bounce 0.3s ease-out" }} />
                  </Box>
                  <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                    All calls done!
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)", mt: 0.5 }}>
                    Great work today 🎉
                  </Typography>
                </Paper>
              ) : (
                <>
                  <List disablePadding>
                    {visibleQueue.map((profile) => (
                      <QueueItem
                        key={profile.id}
                        profile={profile}
                        onSelect={selectProfile}
                        />
                      ))}
                  </List>

                  {!showAll && queue.length > 3 && (
                    <Button
                    fullWidth
                      disableRipple
                      onClick={() => setShowAll(true)}
                      sx={{
                        mt: 1,
                        minHeight: 0,
                        height: "auto",
                        px: 0,
                        py: "12px",
                        textAlign: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        lineHeight: 1.2,
                        textTransform: "none",
                        color: "hsl(150 38% 17%)",
                        borderRadius: "0.75rem",
                        border: "1px solid hsl(140 8% 88%)",
                        backgroundColor: "#ffffff",
                        "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.3)", color: "hsla(150, 38%, 17%, 0.8)" },
                      }}
                      >
                      {`Show ${queue.length - 3} more`}
                    </Button>
                  )}

                  {showAll && queue.length > 3 && (
                    <Button
                      fullWidth
                      disableRipple
                      onClick={() => setShowAll(false)}
                      sx={{
                        mt: 1,
                        minHeight: 0,
                        height: "auto",
                        px: 0,
                        py: "12px",
                        textAlign: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        lineHeight: 1.2,
                        textTransform: "none",
                        color: "hsl(150 10% 46%)",
                        borderRadius: "0.75rem",
                        border: "1px solid hsl(140 8% 88%)",
                        backgroundColor: "#ffffff",
                        "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.3)", color: "hsl(150 30% 12%)" },
                      }}
                      >
                      Show less
                    </Button>
                  )}
                </>
              )}

              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut", delay: 0.24 }}
                sx={{ mt: 3 }}
              >
                <LogCallCard profiles={queue} onLogCall={onQuickLogCall} />
              </Box>

              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut", delay: 0.3 }}
                sx={{ mt: 3 }}
              >
                <FollowedActivityCard />
              </Box>
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut", delay: 0.36 }}
                sx={{ mt: 3 }}
              >
                <ActivityFeedCard />
              </Box>
            </Box>

            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: "easeOut", delay: 0.4 }}
              sx={{ gridColumn: { xs: "span 1", lg: "span 2" }, display: "flex", flexDirection: "column", gap: 3 }}
            >
              <PipelinesCard
                title="Pipelines"
                pipelines={pipelines}
                onCreateClick={() => openCreatePipelineDrawer()}
                onArchive={archivePipeline}
                showArchive={canArchiveSharedPipelines}
                onPipelineClick={(pipeline) => navigate(`/internal/pipelines/${pipeline.id}`)}
                onTogglePin={togglePinPipeline}
                maxVisible={5}
                viewAllHref="/internal/pipelines"
              />

              {/* Top Performers Leaderboard */}
              <TopPerformersCard leaderboard={leaderboard} period={period} />

              <DailySummaryCard summary={outcomeSummary} />

              <PersonalNotesCard
                notes={personalNotes}
                onAdd={addPersonalNote}
                onRemove={removePersonalNote}
              />
            </Box>
          </Box>
        </Box>

        <CreatePipelineDrawer
          open={showCreatePipelineDrawer}
          onClose={() => setShowCreatePipelineDrawer(false)}
          onCreated={() => fetchDashboard(false)}
        />

        <Drawer anchor="right" open={Boolean(selectedProfile)} onClose={() => setSelectedProfile(null)}>
          <Box sx={{ width: { xs: "100vw", sm: 448 }, backgroundColor: "hsl(140 10% 95%)", height: "100%", display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: "1px solid hsl(140 8% 88%)",
                backgroundColor: "rgba(245,247,244,0.95)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: "hsl(150 30% 12%)" }}>
                  {[selectedProfile?.first_name, selectedProfile?.last_name].filter(Boolean).join(" ").trim() || "Unnamed"}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }}>
                  {selectedProfile?.desired_title || "No desired role"}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setSelectedProfile(null)}>
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>

            <Box sx={{ p: 3, overflowY: "auto", flex: 1, "--input-bg": "#FFF" }}>
              {selectedProfile && (
                <ProfileDetailPanel
                  profile={selectedProfile}
                  onLogOutcome={onLogCall}
                  onSignUpToCandidate={handleSignUpToCandidate}
                />
              )}
            </Box>
          </Box>
        </Drawer>

        <ConfirmDialog
          open={Boolean(archiveConfirmId)}
          onClose={() => setArchiveConfirmId(null)}
          onConfirm={confirmArchivePipeline}
          title="Archive pipeline?"
          description="This pipeline and all its profiles will be archived. This action cannot be undone."
          confirmLabel="Archive"
          destructive
        />

      </Box>
    </>
  );
};

export default InternalDashboardPage;
